import { Link } from 'react-router-dom'
import { Section, SectionHeading, NumberCircle, FAQ, CTABand, SplitHero } from '../components/ui'
import { PHONE_DISPLAY, PHONE_TEL } from '../lib/constants'

const previewLabelStyle = {
  margin: 0,
  fontSize: 13,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-sage-deep)',
}

const previewCardStyle = {
  minWidth: 0,
  overflowWrap: 'anywhere',
  borderRadius: 8,
  border: '1px solid var(--color-line)',
  background: '#FFFFFF',
  padding: 24,
  boxShadow: 'var(--shadow-soft)',
}

const accessSteps = [
  { n: '1', title: 'We create your account', body: 'During onboarding, your care coordinator sets up Family Room access for the people you choose.' },
  { n: '2', title: 'You receive an invitation', body: "You'll get an email with instructions to set a password and log in for the first time." },
  { n: '3', title: 'Updates appear automatically', body: "From your caregiver's very first visit, notes, schedules and receipts appear in your portal." },
]

const faqs = [
  { q: 'Who can access the Family Room?', a: 'Any family member or decision-maker you choose can be given a login during onboarding. You control who has access.' },
  { q: 'Is the information in the portal private?', a: 'Yes. The Family Room is a private, password-protected portal used only by your family and your care team.' },
  { q: 'What if I notice something I have questions about?', a: 'You can leave a family note directly in the portal, or call your care coordinator at any time — the portal is a supplement to communication, not a replacement.' },
]

export default function FamilyRoom() {
  return (
    <>
      <SplitHero
        eyebrow="Family Room Portal"
        title="Everything happening at home, in one clear place."
        lede="The Family Room is a private portal where your family can see daily care notes, schedules and billing — so you always know how your loved one's day went."
        image="/assets/family-room.jpg"
        imageAlt="Family member reviewing care updates on a laptop at home"
        actions={
          <>
            <Link to="/schedule-assessment" className="btn btn-primary">Schedule an In-Home Assessment</Link>
            <a href={PHONE_TEL} className="btn btn-secondary">Call {PHONE_DISPLAY}</a>
          </>
        }
      />

      {/* Portal preview cards */}
      <Section tone="cream">
        <SectionHeading
          eyebrow="What You'll See"
          title="A window into every visit"
          lede="After each visit, your caregiver logs what happened. It's available to your family right away."
        />
        <div className="grid grid-3" style={{ marginTop: 48 }}>
          <div style={previewCardStyle}>
            <p style={previewLabelStyle}>Daily Care Notes</p>
            <p style={{ margin: '12px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'var(--color-ink)' }}>
              "Enjoyed a slow morning walk and a favourite lunch. In great spirits, chatted about the garden."
            </p>
          </div>
          <div style={previewCardStyle}>
            <p style={previewLabelStyle}>Schedule</p>
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 17, color: 'var(--color-ink)' }}>
              <span>Tue · 9:00 AM – 1:00 PM · Amara O.</span>
              <span>Thu · 9:00 AM – 1:00 PM · Amara O.</span>
              <span>Sat · 1:00 PM – 3:00 PM · Dale R.</span>
            </div>
          </div>
          <div style={previewCardStyle}>
            <p style={previewLabelStyle}>Family Notes</p>
            <p style={{ margin: '12px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'var(--color-ink)' }}>
              "She has a dentist appointment Friday — could we plan the visit around 2 PM?"
            </p>
          </div>
          <div style={previewCardStyle}>
            <p style={previewLabelStyle}>Billing Receipts</p>
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 17, color: 'var(--color-ink)' }}>
              <span>Week of Apr 8 — 8 hrs — $312.00</span>
              <span>Week of Apr 15 — 6 hrs — $234.00</span>
            </div>
          </div>
          <div style={{ display: 'flex', minWidth: 0, height: '100%', boxSizing: 'border-box', flexDirection: 'column', justifyContent: 'center', borderRadius: 8, background: 'var(--color-teal-800)', padding: 24, color: 'var(--color-cream)' }}>
            <p style={{ margin: 0, fontFamily: 'var(--font-serif)', fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.2 }}>See it for yourself</p>
            <p style={{ margin: '8px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'oklch(0.9765 0.0098 87.47 / 85%)' }}>
              Existing clients can log in any time to review the latest updates.
            </p>
            <div style={{ marginTop: 20 }}>
              <Link to="/login" className="btn btn-on-dark">Open the Family Room Portal</Link>
            </div>
          </div>
        </div>
      </Section>

      {/* How access works */}
      <Section tone="panel">
        <SectionHeading
          eyebrow="Getting Access"
          title="How access works"
          lede="Setting up the Family Room is part of onboarding — no extra steps for your family to manage."
        />
        <div className="grid grid-3" style={{ marginTop: 48, gap: 40 }}>
          {accessSteps.map((s) => (
            <div key={s.n} style={{ display: 'flex', gap: 20, position: 'relative', zIndex: 1 }}>
              <span style={{ marginTop: 4 }}><NumberCircle n={s.n} /></span>
              <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                <h3 style={{ margin: 0, fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-teal-900)' }}>{s.title}</h3>
                <p style={{ margin: '8px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'var(--color-muted)' }}>{s.body}</p>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 40, display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          <Link to="/login" className="btn btn-primary">Open the Family Room Portal</Link>
          <Link to="/schedule-assessment" className="btn btn-secondary">Schedule an In-Home Assessment</Link>
        </div>
      </Section>

      {/* Access note */}
      <Section tone="cream">
        <div style={{ borderRadius: 8, border: '1px solid var(--color-line)', background: 'var(--color-panel-deep)', padding: 24, fontSize: 17, lineHeight: 1.6, color: 'var(--color-muted)' }}>
          Family Room Portal access is set up as part of onboarding once care begins. If you're already a True North client and haven't received an invitation, please <Link to="/contact" style={{ fontWeight: 600, color: 'var(--color-teal-800)', textDecoration: 'underline', textUnderlineOffset: 4 }}>contact our office</Link> and we'll get you set up.
        </div>
      </Section>

      {/* FAQ */}
      <Section tone="panel">
        <FAQ items={faqs} />
      </Section>

      <CTABand
        title="See the Family Room for yourself"
        lede="Log in if you're an existing client, or schedule an assessment to get started with True North."
      />
    </>
  )
}
